import apiClient from '@/lib/api-client'
import { OFFICIAL_EXTENSION_IDS } from '@/lib/env'

export type ExtensionAuthContext = {
  source: 'extension'
  extensionId: string
  handoffNonce: string
}

export type ExtensionHandoffOptions = {
  requestedOrganizationId?: string | null
  closeSenderTab?: boolean
  dashboardAccessToken?: string | null
}

export type ExtensionHandoffResponse = {
  tokens: {
    access: string
    refresh: string
  }
  user: {
    email: string
    [key: string]: unknown
  }
  organization_id: string | null
  active_organization?: unknown
  organizations?: unknown[]
}

const CHROME_EXTENSION_ID_RE = /^[a-p]{32}$/
const LAST_EXTENSION_ID_STORAGE_KEY = 'mphelper:lastExtensionId'

export function isChromeExtensionId(value: string | null | undefined): value is string {
  return Boolean(value && CHROME_EXTENSION_ID_RE.test(value))
}

// A well-formed id is not enough: auth tokens may only be handed off to the
// official extension build(s). The id in the login URL/OAuth state is attacker-
// influenceable, so it is checked against the configured allowlist before we
// mint or dispatch any tokens. When no allowlist resolves (misconfigured build)
// we degrade to the format-only check rather than block all logins.
export function isAllowedExtensionId(value: string | null | undefined): value is string {
  if (!isChromeExtensionId(value)) {
    return false
  }
  if (OFFICIAL_EXTENSION_IDS.length === 0) {
    return true
  }
  return OFFICIAL_EXTENSION_IDS.includes(value)
}

export function getExtensionAuthContext(search = window.location.search): ExtensionAuthContext | null {
  const params = new URLSearchParams(search)
  const source = params.get('source')
  const extensionId = params.get('extension_id') || ''
  const handoffNonce = params.get('handoff_nonce') || ''

  if (source !== 'extension' || !isAllowedExtensionId(extensionId) || !handoffNonce) {
    return null
  }

  return {
    source: 'extension',
    extensionId,
    handoffNonce,
  }
}

export function getExtensionAuthContextFromState(state: any): ExtensionAuthContext | null {
  const source = state?.source
  const extensionId = typeof state?.extension_id === 'string' ? state.extension_id : ''
  const handoffNonce = typeof state?.handoff_nonce === 'string' ? state.handoff_nonce : ''

  if (source !== 'extension' || !isAllowedExtensionId(extensionId) || !handoffNonce) {
    return null
  }

  return {
    source: 'extension',
    extensionId,
    handoffNonce,
  }
}

export function encodeAuthState(value: unknown): string {
  return btoa(JSON.stringify(value))
}

export function sendAuthToExtension(extensionId: string, payload: any): Promise<void> {
  return new Promise((resolve, reject) => {
    const chromeRuntime = (window as any).chrome?.runtime
    if (!chromeRuntime?.sendMessage) {
      reject(new Error('Chrome extension messaging is not available.'))
      return
    }
    chromeRuntime.sendMessage(extensionId, { type: 'MPHELPER_EXTENSION_AUTH', payload }, (response: any) => {
      const lastError = chromeRuntime.lastError
      if (lastError) {
        reject(new Error(lastError.message))
        return
      }
      if (!response?.success) {
        reject(new Error(response?.error || 'Extension rejected auth payload.'))
        return
      }
      resolve()
    })
  })
}

export function sendLogoutToExtension(extensionId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const chromeRuntime = (window as any).chrome?.runtime
    if (!chromeRuntime?.sendMessage) {
      reject(new Error('Chrome extension messaging is not available.'))
      return
    }
    chromeRuntime.sendMessage(extensionId, { type: 'MPHELPER_EXTENSION_LOGOUT' }, (response: any) => {
      const lastError = chromeRuntime.lastError
      if (lastError) {
        reject(new Error(lastError.message))
        return
      }
      if (!response?.success) {
        reject(new Error(response?.error || 'Extension rejected logout payload.'))
        return
      }
      resolve()
    })
  })
}

export async function notifyExtensionLogout(): Promise<void> {
  const extensionId = localStorage.getItem(LAST_EXTENSION_ID_STORAGE_KEY)
  if (!isChromeExtensionId(extensionId)) {
    return
  }

  try {
    await sendLogoutToExtension(extensionId)
  } finally {
    localStorage.removeItem(LAST_EXTENSION_ID_STORAGE_KEY)
  }
}

export async function createExtensionHandoffSession(
  requestedOrganizationId?: string | null,
  dashboardAccessToken?: string | null,
): Promise<ExtensionHandoffResponse> {
  const payload = requestedOrganizationId
    ? { requested_organization_id: requestedOrganizationId }
    : {}
  const response = await apiClient.post('/api/v1/auth/extension/handoff-session/', payload, {
    headers: dashboardAccessToken
      ? { Authorization: `Bearer ${dashboardAccessToken}` }
      : undefined,
  })
  return response.data as ExtensionHandoffResponse
}

export async function completeExtensionHandoff(
  context: ExtensionAuthContext,
  options: ExtensionHandoffOptions = {},
): Promise<ExtensionHandoffResponse> {
  if (!isAllowedExtensionId(context.extensionId)) {
    throw new Error('Unrecognized extension id for auth handoff.')
  }
  const requestedOrganizationId =
    options.requestedOrganizationId ||
    localStorage.getItem('lastActiveOrgId') ||
    ''
  const handoff = await createExtensionHandoffSession(requestedOrganizationId, options.dashboardAccessToken)
  await sendAuthToExtension(context.extensionId, {
    tokens: handoff.tokens,
    user: handoff.user,
    organization_id: handoff.organization_id,
    active_organization: handoff.active_organization,
    organizations: handoff.organizations,
    handoff_nonce: context.handoffNonce,
    close_sender_tab: Boolean(options.closeSenderTab),
  })
  localStorage.setItem(LAST_EXTENSION_ID_STORAGE_KEY, context.extensionId)
  return handoff
}
